import React, { useState, useEffect, useCallback } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { ChevronLeft, ChevronRight, X, Maximize2 } from 'lucide-react';

/**
 * CarGallery — premium carousel for the vehicle detail page.
 * - Main carousel with smooth swipe + arrows + counter (1 / N)
 * - Thumbnail strip below (auto-scrolls so the active thumb is visible)
 * - Fullscreen lightbox on click
 */
const CarGallery = ({ images = [], make = '', model = '' }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, align: 'start' });
  const [thumbsRef, thumbsApi] = useEmblaCarousel({
    containScroll: 'keepSnaps',
    dragFree: true,
    align: 'start',
  });
  const [selected, setSelected] = useState(0);
  const [lightbox, setLightbox] = useState(false);

  const onSelect = useCallback(() => {
    if (!emblaApi || !thumbsApi) return;
    const idx = emblaApi.selectedScrollSnap();
    setSelected(idx);
    thumbsApi.scrollTo(idx);
  }, [emblaApi, thumbsApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on('select', onSelect);
    onSelect();
    return () => emblaApi.off('select', onSelect);
  }, [emblaApi, onSelect]);

  const scrollTo = (i) => emblaApi && emblaApi.scrollTo(i);

  // ESC to close lightbox + arrow navigation
  useEffect(() => {
    if (!lightbox) return;
    const handler = (e) => {
      if (e.key === 'Escape') setLightbox(false);
      if (e.key === 'ArrowRight' && emblaApi) emblaApi.scrollNext();
      if (e.key === 'ArrowLeft' && emblaApi) emblaApi.scrollPrev();
    };
    window.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [lightbox, emblaApi]);

  if (!images.length) return null;

  return (
    <div className="space-y-3" data-testid="car-gallery">
      {/* MAIN CAROUSEL */}
      <div className="relative group/main">
        <div className="overflow-hidden bg-slate-100 aspect-[4/3] rounded-2xl" ref={emblaRef}>
          <div className="flex h-full">
            {images.map((img, i) => (
              <div key={i} className="relative flex-[0_0_100%] min-w-0 h-full">
                <img
                  src={img}
                  alt={`${make} ${model} - photo ${i + 1}`}
                  onClick={() => setLightbox(true)}
                  className="w-full h-full object-cover cursor-zoom-in"
                  data-testid={`car-gallery-image-${i}`}
                />
              </div>
            ))}
          </div>
        </div>

        {images.length > 1 && (
          <>
            <button
              type="button"
              onClick={() => emblaApi && emblaApi.scrollPrev()}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white text-slate-900 border border-slate-200 rounded-full flex items-center justify-center transition-all opacity-0 group-hover/main:opacity-100 shadow-md"
              aria-label="Photo précédente"
              data-testid="gallery-prev"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              type="button"
              onClick={() => emblaApi && emblaApi.scrollNext()}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white text-slate-900 border border-slate-200 rounded-full flex items-center justify-center transition-all opacity-0 group-hover/main:opacity-100 shadow-md"
              aria-label="Photo suivante"
              data-testid="gallery-next"
            >
              <ChevronRight size={20} />
            </button>

            {/* Counter */}
            <div className="absolute bottom-4 left-4 bg-white/90 text-slate-900 text-xs font-semibold px-3 py-1.5 rounded-full shadow-sm">
              {selected + 1} / {images.length}
            </div>

            {/* Fullscreen */}
            <button
              type="button"
              onClick={() => setLightbox(true)}
              className="absolute bottom-4 right-4 w-9 h-9 bg-white/90 hover:bg-white text-slate-900 border border-slate-200 rounded-full flex items-center justify-center shadow-sm"
              title="Voir en plein écran"
              data-testid="gallery-fullscreen"
            >
              <Maximize2 size={14} />
            </button>
          </>
        )}
      </div>

      {/* THUMBNAILS STRIP — horizontal scroll */}
      {images.length > 1 && (
        <div className="overflow-hidden" ref={thumbsRef}>
          <div className="flex gap-2">
            {images.map((img, i) => (
              <button
                key={i}
                type="button"
                onClick={() => scrollTo(i)}
                className={`relative flex-[0_0_22%] sm:flex-[0_0_15%] md:flex-[0_0_12%] aspect-square overflow-hidden rounded-lg border-2 transition-all ${
                  selected === i
                    ? 'border-blue-900 opacity-100'
                    : 'border-slate-200 opacity-70 hover:opacity-100'
                }`}
                data-testid={`car-thumbnail-${i}`}
                aria-label={`Voir photo ${i + 1}`}
              >
                <img src={img} alt={`miniature ${i + 1}`} className="w-full h-full object-cover" />
                {selected === i && (
                  <span className="absolute inset-0 bg-blue-900/0" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* LIGHTBOX */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center"
          data-testid="gallery-lightbox"
        >
          <button
            type="button"
            onClick={() => setLightbox(false)}
            className="absolute top-6 right-6 w-11 h-11 bg-white/10 hover:bg-white hover:text-slate-900 text-white border border-white/20 rounded-full flex items-center justify-center transition-colors"
            aria-label="Fermer"
            data-testid="lightbox-close"
          >
            <X size={20} />
          </button>

          <div className="absolute top-6 left-6 text-white text-sm font-semibold">
            {selected + 1} / {images.length}
          </div>

          <button
            type="button"
            onClick={() => emblaApi && emblaApi.scrollPrev()}
            className="absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white hover:text-slate-900 text-white border border-white/20 rounded-full flex items-center justify-center transition-colors"
            aria-label="Précédent"
          >
            <ChevronLeft size={22} />
          </button>
          <button
            type="button"
            onClick={() => emblaApi && emblaApi.scrollNext()}
            className="absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white hover:text-slate-900 text-white border border-white/20 rounded-full flex items-center justify-center transition-colors"
            aria-label="Suivant"
          >
            <ChevronRight size={22} />
          </button>

          <img
            src={images[selected]}
            alt={`${make} ${model} - photo ${selected + 1}`}
            className="max-w-[92vw] max-h-[88vh] object-contain"
          />
        </div>
      )}
    </div>
  );
};

export default CarGallery;
